<div #trigger="cdkOverlayOrigin" cdkOverlayOrigin class="w-full">
  @if (!selectedItems.length) {
    @if (!isReadonly) {
      <app-new-button
        data-test-id="toggle-btn"
        class="toggle-btn"
        [type]="'secondary'"
        [svgIconPath]="buttonIcon"
        (click)="toggleDropdown()"
      >
        {{ buttonTranslationKey | translate }}
      </app-new-button>
    } @else {
      <span class="placeholder-text">
        {{ placeholderTranslationKey | translate }}
      </span>
    }

    <ng-template #placeholder>
      <span class="placeholder-text">
        {{ placeholderTranslationKey | translate }}
      </span>
    </ng-template>
  } @else {
    @if (displayAllSelectedItems) {
      <div data-test-id="labels" class="labels" [class.flex-nowrap]="displaySelectedItemsInline">
        @for (item of selectedLabels; track itemsTrackByFn.bind(this)($index, item)) {
          <app-label
            data-test-id="label"
            [class.cursor-pointer]="!isReadonly"
            [class.item-length-limit]="!displaySelectedItemsFully"
            [icon]="labelIcon"
            [type]="labelType"
            [name]="getDisplayValue(item)"
            (click)="!isReadonly && toggleDropdown()"
          ></app-label>
        }

        @if (!isReadonly && hasAddLabel) {
          <button class="add-label" (click)="toggleDropdown()">
            <svg-icon class="w-3 h-3" [src]="'Add'"></svg-icon>
          </button>
        }
      </div>
    } @else {
      <div class="flex items-center">
        <div
          class="labels flex-nowrap"
          displayingElementsNumber
          [elementsGap]="8"
          (numberOfDisplayedElements)="changeDisplayedItems($event)"
        >
          @for (item of selectedLabels; track itemsTrackByFn.bind(this)($index, item)) {
            <app-label
              [class.cursor-pointer]="!isReadonly"
              [class.item-length-limit]="!displaySelectedItemsFully"
              [class.invisible]="numberOfDisplayedItems === undefined"
              [icon]="labelIcon"
              [type]="labelType"
              [name]="getDisplayValue(item)"
              (click)="!isReadonly && toggleDropdown()"
            ></app-label>
          }
        </div>
        <app-label
          #tooltipOnPreview="ngbTooltip"
          data-test-id="hidden-items-amount"
          class="hidden-items-amount ml-2 flex"
          placement="top bottom"
          tooltipClass="default-tooltip other-items-tooltip"
          [class.cursor-pointer]="!isReadonly"
          [class.invisible]="numberOfDisplayedItems === undefined"
          [class.hidden]="!numberOfHiddenItems"
          [name]="numberOfHiddenItems"
          [type]="labelType"
          [showIcons]="false"
          [hasTooltip]="false"
          [ngbTooltip]="otherItems"
          [keepOnHover]="true"
          (click)="!isReadonly && toggleDropdown(); closeTooltip()"
        ></app-label>
      </div>
    }
  }

  <ng-template #otherItems>
    <div class="tooltip-container">
      @for (item of partOfHiddenItems; track itemsTrackByFn.bind(this)($index, item)) {
        <span class="tooltip-item" (click)="!isReadonly && toggleDropdown(); !isReadonly && closeTooltip()">
          {{ getDisplayValue(item) }}
        </span>
      }
    </div>
  </ng-template>
</div>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayHasBackdrop]="true"
  [cdkConnectedOverlayPositions]="doesDropdownOverlapsButton ? responsiveOverlayPositions : undefined"
  [cdkConnectedOverlayBackdropClass]="'cdk-overlay-transparent-backdrop'"
  [cdkConnectedOverlayOrigin]="trigger"
  [cdkConnectedOverlayOpen]="isDropdownOpened"
  [cdkConnectedOverlayPanelClass]="overlayPanelClass"
  (backdropClick)="close()"
>
  <app-dropdown-options-backdrop #openableElement class="min-w-86" [style.width.px]="listWidth">
    @if (displaySelectedItemsList && selectedItems.length) {
      <div class="labels-container labels">
        @for (item of selectedItems; track itemsTrackByFn.bind(this)($index, item)) {
          <app-label
            class="max-w-full"
            [attr.data-test-id]="getItemId(item)"
            [name]="getDisplayValue(item)"
            [type]="labelType"
            [isEditingMode]="true"
            (removeLabel)="unselectItem(item); updateOverlayPosition()"
          ></app-label>
        }
      </div>
    }

    @if (searchEnabled) {
      <app-dropdown-search-input
        #dropdownSearch
        [formControl]="searchField"
        [clearButtonEnabled]="true"
        [autoFocus]="true"
        [placeholder]="searchFieldPlaceholder"
        [maxLength]="maxLength"
        (input)="handleSearchInput()"
        (click)="searchFieldClick($event)"
      ></app-dropdown-search-input>
    }

    @if (data?.length) {
      <perfect-scrollbar #parentScrollRef [style.max-height.px]="listMaxHeight" [config]="{ suppressScrollX: true }">
        <virtual-scroller
          #scroller
          [parentScroll]="$any(parentScrollRef)?.directiveRef?.elementRef.nativeElement"
          [items]="allLabels"
          [bufferAmount]="30"
          [enableUnequalChildrenSizes]="true"
        >
          @for (item of $any(scroller).viewPortItems; track itemsTrackByFn.bind(this)) {
            <app-dropdown-option
              [attr.data-test-id]="getItemId(item)"
              [style.height.px]="dropdownOptionHeight"
              [class.h-auto]="doesOptionShouldBeShownFully"
              [class.hidden]="dictionary[getItemId(item)]"
              (click)="selectItem(item); updateOverlayPosition()"
            >
              @if (shouldTruncate) {
                <span
                  class="truncate"
                  [ngbTooltip]="getDisplayValue(item)"
                  [openTooltipWhenOverflowedAndHovered]="true"
                  [openOnWidthOverflow]="true"
                  [placement]="'top'"
                >
                  {{ getDisplayValue(item) }}
                </span>
              } @else {
                {{ getDisplayValue(item) }}
              }
            </app-dropdown-option>
          }
        </virtual-scroller>
      </perfect-scrollbar>
    }

    @if (displayEmptyState && !data?.length) {
      <span class="empty-state">
        {{ emptyListPlaceholder | translate }}
      </span>
    }

    @if (isAddOptionShown) {
      <app-add-option
        [entityNameText]="entityNameText"
        [value]="searchField.value"
        (click)="addNewOption()"
      ></app-add-option>
    }

    @if (isBottomOptionShown) {
      <app-dropdown-option
        data-test-id="bottom-option"
        class="bottom-option"
        [context]="searchField.value"
        [style.height.px]="dropdownOptionHeight"
        [menuAction]="bottomDropdownAction"
      ></app-dropdown-option>
    }
  </app-dropdown-options-backdrop>
</ng-template>
